/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import showBatchOperationStartedToast from '../batchOperations/helpers/showBatchOperationStartedToast';
import { OPERATION_TYPES, RESULT } from '../batchOperations/constants';
import { pruneSelection } from './helpers/applySelection';
import handleStoreDisconnectedError, {
  isStoreDisconnectedError,
} from './helpers/handleStoreDisconnectedError';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;

const isSamePublishedContext = (requestContext, currentContext) => (
  requestContext?.type === 'channel'
  && requestContext.recordState === 'published'
  && currentContext?.type === 'channel'
  && currentContext.recordState === 'published'
  && String(currentContext.channelId) === String(requestContext.channelId)
);

export const deleteLiveListingsFromChannel = createAsyncThunk(
  'products/deleteLiveListingsFromChannel',
  async ({
    productIds = [],
    variantIds = [],
    wipeListingIds = [],
    removedVariantIds = [],
    channelName,
    requestContext = null,
    requestKeys = [],
  }, {
    extra: {
      sdk, Toast, Logger,
    },
  }) => {
    try {
      const { data } = await sdk.products.deleteLiveListings({
        ...(productIds.length ? { productIds } : {}),
        ...(variantIds.length ? { variantIds } : {}),
      });

      if (data?.batchId) {
        showBatchOperationStartedToast({
          batchId: data.batchId,
          status: data.status || RESULT.queued,
          total: data.total,
          operationType: data.operationType || OPERATION_TYPES.DELETE_FROM_CHANNEL,
          channelName,
        });
        return {
          ...data,
          wipeListingIds,
          removedVariantIds,
          requestContext,
          requestKeys,
        };
      }

      Toast(strings.deleteFromChannelFailed, { type: 'error', autoClose: false });
      throw new Error('Delete from channel batch was not started');
    } catch (error) {
      if (isStoreDisconnectedError(error)) {
        handleStoreDisconnectedError({ Toast, error });
      } else {
        Toast(strings.deleteFromChannelFailed, { type: 'error', autoClose: false });
      }
      Logger.error(new Error('Bulk delete live listings failed'), { error }, [
        'DeleteLiveListingsFromChannelFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(deleteLiveListingsFromChannel.fulfilled, (state, { payload }) => {
      const wipedListings = new Set((payload?.wipeListingIds || []).map(String));
      const removedVariants = new Set((payload?.removedVariantIds || []).map(String));
      if (!wipedListings.size && !removedVariants.size) {
        state.error = null;
        return;
      }

      const requestContext = payload.requestContext;
      const currentContext = state.ui.selection.context;

      if (isSamePublishedContext(requestContext, currentContext)) {
        const prevLength = state.search.items.length;
        state.search.items = state.search.items.filter((item) => {
          const id = String(item._id);
          const base = item.baseProductId != null ? String(item.baseProductId) : '';
          if (wipedListings.has(id) || wipedListings.has(base)) return false;
          if (removedVariants.has(id)) return false;
          return true;
        });
        const removedCount = prevLength - state.search.items.length;
        if (removedCount > 0) {
          state.search.total = Math.max(0, (state.search.total || 0) - removedCount);
        }
      }

      const requestKeySet = new Set((payload.requestKeys || []).map(String));
      const keysToRemove = state.ui.selection.keys.filter((key) => {
        if (requestKeySet.size && !requestKeySet.has(String(key))) return false;
        const entry = state.ui.selection.byKey[key];
        if (!entry) return false;
        if (entry.type === 'listing' && wipedListings.has(String(entry._id))) return true;
        if (entry.type === 'listingVariant' && wipedListings.has(String(entry.baseProductId))) {
          return true;
        }
        if (entry.type === 'listingVariant' && removedVariants.has(String(entry._id))) {
          return true;
        }
        return false;
      });

      state.ui.selection = pruneSelection(state.ui.selection, keysToRemove);
      state.error = null;
    })
    .addCase(deleteLiveListingsFromChannel.rejected, (state, { error }) => {
      state.error = error?.message || 'Cannot delete selected listings from channel';
    });
};
